

























































































































































































































































import { Component, Prop, Watch, Vue } from "vue-property-decorator";

import { endpoints } from "@/endpoints";
import { eventLoading } from "@/components/sce/EventLoading";
import { fmtInt, fmtFloat } from "./formatters";
import { buildRcaUrl, RcaFilters } from "./filterQueryParams";
import HelpIcon from "./HelpIcon.vue";
import ScenarioWindowFootnote from "./ScenarioWindowFootnote.vue";

interface PanelState {
    loading: boolean;
    error: string | null;
    data: any;
}

function newPanelState(): PanelState {
    return { loading: false, error: null, data: null };
}

interface LinkOption {
    link_id: number;
    road_name: string;
    label: string;
}

/**
 * Hotspots-tab's SelectedExposurePanel. Eevery metric is aggregated across the global date range. Numbers follow the
 * dashboard's daily-sum convention (a trip/segment contributes on each day it qualifies).
 */
@Component({ components: { HelpIcon, ScenarioWindowFootnote } })
export default class HotspotsSelectedExposurePanel extends Vue {
    @Prop({ required: true }) startDate!: string;
    @Prop({ required: true }) endDate!: string;
    /** Catalog payload — kept for prop parity with the sibling daily panel. */
    @Prop({ default: null }) catalog!: any;
    /** Global sidebar filters — narrow selected-exposure facts to matching trips. */
    @Prop({ default: () => ({}) }) filters!: RcaFilters;

    selectionHelp =
        "Choose whether to drill into a single closed road segment or every closed road segment sharing the same road name, aggregated across the selected date range. A road-name selection combines all of that road's closed road segments and counts each trip-day once.";

    /**
     * Closed-segment rows visible in the Hotspots map / table — the source of the Road segment /
     * Road dropdown options. The user can only pick segments that appear in the current range.
     */
    @Prop({ default: () => [] }) mapRows!: any[];
    /**
     * Most-recently clicked map dot (or table drill button). When this changes we apply the field
     * that matches the current selection mode — clicking a dot in "Road name" mode picks that
     * segment's road name.
     */
    @Prop({ default: null }) clickedRow!: {
        link_id: number;
        road_name: string;
    } | null;

    fmtInt = fmtInt;
    fmtFloat = fmtFloat;

    selectionLevel: "link_id" | "road_name" = "link_id";
    linkSelection: number | null = null;
    roadSelection: string | null = null;

    summary: PanelState = newPanelState();
    commodity: PanelState = newPanelState();
    enterpriseTypes: PanelState = newPanelState();

    /** True while any of the panel's fetches is in flight — disables the selection controls. */
    get panelLoading(): boolean {
        return (
            this.summary.loading ||
            this.commodity.loading ||
            this.enterpriseTypes.loading
        );
    }

    /** Active enterprise-type tab (0 = origin, 1 = destination). */
    entTab = 0;

    mounted() {
        this.applyClickedRow();
        this.ensureDefaultSelection();
        this.fetchAll();
    }

    @Watch("clickedRow")
    onClickedRowChanged() {
        this.applyClickedRow();
        this.fetchAll();
    }

    /** One key over every fetch input so a param change triggers exactly one fetch. */
    private get fetchParamsKey(): string {
        return `${this.startDate}|${this.endDate}|${JSON.stringify(
            this.filters,
        )}`;
    }

    @Watch("fetchParamsKey")
    onDateContextChanged() {
        this.fetchAll();
    }

    @Watch("mapRows", { deep: false })
    onMapRowsChanged() {
        // Default to the top-ranked segment on first load so the panel shows something useful.
        this.ensureDefaultSelection();
        // If the user's current pick is still visible, leave it; otherwise fall back to the first
        // row (the top of the hotspots table, ordered by closure activity in the SQL).
        const firstRow = this.mapRows?.[0] ?? null;
        if (
            this.selectionLevel === "link_id" &&
            this.linkSelection != null &&
            !this.linkOptions.some((o) => o.link_id === this.linkSelection)
        ) {
            this.linkSelection = firstRow?.link_id ?? null; // @Watch refetches
        }
        if (
            this.selectionLevel === "road_name" &&
            this.roadSelection &&
            !this.roadOptions.includes(this.roadSelection)
        ) {
            this.roadSelection = firstRow?.road_name ?? null; // @Watch refetches
        }
    }

    /** Seed a selection from the first available row when the user has not picked one yet. */
    ensureDefaultSelection() {
        if (this.hasSelection) return;
        const firstRow = this.mapRows?.[0];
        if (!firstRow) return;
        if (this.selectionLevel === "link_id") {
            this.linkSelection = firstRow.link_id ?? null;
        } else {
            this.roadSelection = firstRow.road_name ?? null;
        }
    }

    /** Apply the latest map click to whichever field matches the current selection mode. */
    applyClickedRow() {
        if (!this.clickedRow) return;
        if (this.selectionLevel === "link_id") {
            this.linkSelection = this.clickedRow.link_id;
        } else {
            this.roadSelection = this.clickedRow.road_name || null;
        }
    }

    /** Toggling Road segment <-> Road name keeps the same underlying selection at a new grain. */
    onSelectionLevelChange() {
        if (this.selectionLevel === "road_name") {
            const fromLink = this.mapRows.find(
                (r) => r.link_id === this.linkSelection,
            );
            const candidate =
                fromLink?.road_name ||
                this.clickedRow?.road_name ||
                this.roadOptions[0] ||
                null;
            this.roadSelection = candidate;
        } else {
            let candidate: number | null = null;
            if (this.roadSelection) {
                const match = this.mapRows.find(
                    (r) => r.road_name === this.roadSelection,
                );
                if (match) candidate = match.link_id;
            }
            if (candidate == null && this.clickedRow) {
                candidate = this.clickedRow.link_id;
            }
            if (candidate == null && this.linkOptions.length) {
                candidate = this.linkOptions[0].link_id;
            }
            this.linkSelection = candidate;
        }
        this.fetchAll();
    }

    @Watch("linkSelection")
    @Watch("roadSelection")
    onSelectionValueChanged() {
        this.fetchAll();
    }

    /**
     * Called by HotspotsTab when the user clicks a corridor bar in the "highest
     * closure activity" chart or a Road corridor hotspots row. A corridor is a road, so
     * switch to road-name grain and drill into that road. */
    selectRoad(roadName: string) {
        if (!roadName) return;
        this.selectionLevel = "road_name";
        if (this.roadSelection === roadName) {
            // Same road already selected — the roadSelection watcher won't refire, so
            // refetch explicitly (the grain may have just changed from link to road).
            this.fetchAll();
        } else {
            this.roadSelection = roadName;
        }
    }

    /**
     * Called by HotspotsTab when the user clicks a Closed road segment hotspots
     * row. Switch to road-segment (link) grain and drill into that segment. */
    selectLink(linkId: number) {
        if (linkId == null) return;
        this.selectionLevel = "link_id";
        if (this.linkSelection === linkId) {
            // Same segment already selected — the linkSelection watcher won't refire, so
            // refetch explicitly (the grain may have just changed from road to link).
            this.fetchAll();
        } else {
            this.linkSelection = linkId;
        }
    }

    get linkOptions(): LinkOption[] {
        const seen = new Set<number>();
        const out: LinkOption[] = [];
        const pushOption = (linkId: number | null, roadName: string) => {
            if (linkId == null || seen.has(linkId)) return;
            seen.add(linkId);
            const road = roadName || "(unnamed road)";
            out.push({
                link_id: linkId,
                road_name: road,
                label: `${road} (link_id=${linkId})`,
            });
        };
        for (const r of this.mapRows) {
            pushOption(r.link_id, r.road_name);
        }
        // Always include the currently-selected segment, even if it ranked outside the
        // hotspot table — clicking a map dot can select a link that isn't in
        // ``mapRows``, which causes the autocomplete to render blank. The clicked dot carries the
        // road name; fall back to it so the autocomplete populates properly.
        if (this.linkSelection != null && !seen.has(this.linkSelection)) {
            const clickedName =
                this.clickedRow &&
                this.clickedRow.link_id === this.linkSelection
                    ? this.clickedRow.road_name
                    : "";
            pushOption(this.linkSelection, clickedName);
        }
        out.sort((a, b) => a.label.localeCompare(b.label));
        return out;
    }

    get roadOptions(): string[] {
        const seen = new Set<string>();
        for (const r of this.mapRows) {
            if (r.road_name) seen.add(r.road_name);
        }
        if (this.roadSelection) seen.add(this.roadSelection);
        return Array.from(seen).sort();
    }

    get hasSelection(): boolean {
        return this.selectionLevel === "link_id"
            ? this.linkSelection != null
            : !!this.roadSelection;
    }

    get currentSelectionValue(): number | string | null {
        return this.selectionLevel === "link_id"
            ? this.linkSelection
            : this.roadSelection;
    }

    get summaryRow(): any {
        const data = this.summary.data;
        return Array.isArray(data) && data.length ? data[0] : null;
    }

    /** Amber banner: trips DO traverse the selection (baseline exposed > 0) but none were
     * impacted by the closure (impacted == 0), so every metric except the baseline ones is
     * zero.*/
    get hasNoTransitMovements(): boolean {
        const r = this.summaryRow;
        if (!r) return false;
        const impacted = Number(r.impacted_exposed_trip_count) || 0;
        const baseline = Number(r.baseline_exposed_input_trip_count) || 0;
        return impacted === 0 && baseline > 0;
    }

    /** Red banner: no transit movement's baseline route traversed the selection at all
     * (baseline exposed == 0, which forces impacted == 0 too as there are simply no trips on a selected segment). */
    get hasNoBaselineTrips(): boolean {
        const r = this.summaryRow;
        if (!r) return false;
        return (Number(r.baseline_exposed_input_trip_count) || 0) === 0;
    }

    get kpis() {
        const r = this.summaryRow;
        if (!r) return [];
        return [
            {
                key: "selected_segments",
                label: "Selected closed segment-days",
                tooltip:
                    "Closed-segment occurrences covered by this selection, summed across the date range (a segment closed on several days counts once per day).",
                value: fmtInt(r.selected_link_count),
            },
            {
                key: "impacted_trips",
                label: "Impacted trips *",
                tooltip:
                    "Baseline trip-days that were impacted in the closure scenario, summed across the date range.",
                value: fmtInt(r.impacted_exposed_trip_count),
            },
            {
                key: "road_cost",
                label: "Total extra transport cost *",
                tooltip:
                    "Modelled scenario-vs-baseline change in road cost for the selected trips, summed across the date range.",
                value: `$${fmtFloat(r.road_delta_total_cost, 0)}`,
            },
            {
                key: "road_time",
                label: "Total extra transport hours *",
                tooltip:
                    "Scenario-vs-baseline change in road time (hours), summed across the date range.",
                value: `${fmtFloat(r.road_delta_time_hours, 1)} hrs`,
            },
            {
                key: "road_distance",
                label: "Total extra transport kilometres *",
                tooltip:
                    "Scenario-vs-baseline change in road distance (km), summed across the date range.",
                value: `${fmtFloat(r.road_delta_length_km, 0)} km`,
            },
        ];
    }

    fetchAll() {
        if (!this.hasSelection) return;
        const value = this.currentSelectionValue;
        if (value == null) return;
        const baseParams = {
            start_date: this.startDate,
            end_date: this.endDate,
            selection_type: this.selectionLevel,
            selection_value: value,
        };
        this.fetchPanel(
            this.summary,
            endpoints.roadClosureAnalyticsSelectedExposureRangeSummaryUrl,
            baseParams,
        );
        const drillLimit = 25;
        this.fetchPanel(
            this.commodity,
            endpoints.roadClosureAnalyticsSelectedExposureRangeCommodityUrl,
            { ...baseParams, limit: drillLimit },
        );
        this.fetchPanel(
            this.enterpriseTypes,
            endpoints.roadClosureAnalyticsSelectedExposureRangeEnterpriseTypeUrl,
            { ...baseParams, limit: drillLimit },
        );
    }

    fetchPanel(panel: PanelState, url: string, params: Record<string, any>) {
        panel.loading = true;
        panel.error = null;
        eventLoading.startLoading();
        Vue.axios({
            url: buildRcaUrl(url, params, this.filters),
        })
            .then((response: any) => {
                panel.data = response.data ?? null;
            })
            .catch((err: any) => {
                console.error("[RCA HotspotsSelectedExposure]", err);
                panel.error = err?.message || "Failed to load.";
                panel.data = null;
            })
            .finally(() => {
                panel.loading = false;
                eventLoading.stopLoading();
            });
    }

    commodityHeaders = [
        { text: "Commodity", value: "commodity" },
        {
            text: "Baseline trips *",
            value: "baseline_exposed_input_trip_count",
            align: "end",
        },
        {
            text: "Baseline tonnes *",
            value: "baseline_exposed_input_tonnes",
            align: "end",
        },
        {
            text: "Impacted trips *",
            value: "impacted_exposed_trip_count",
            align: "end",
        },
        {
            text: "Impacted tonnes *",
            value: "impacted_exposed_tonnes",
            align: "end",
        },
        {
            text: "Rerouted trips *",
            value: "rerouted_exposed_trip_count",
            align: "end",
        },
        {
            text: "Rerouted tonnes *",
            value: "rerouted_exposed_tonnes",
            align: "end",
        },
        {
            text: "Blocked trips *",
            value: "blocked_exposed_trip_count",
            align: "end",
        },
        {
            text: "Blocked tonnes *",
            value: "blocked_exposed_tonnes",
            align: "end",
        },
        {
            text: "Total extra transport cost *",
            value: "road_delta_total_cost",
            align: "end",
        },
        {
            text: "Total extra transport hours *",
            value: "road_delta_time_hours",
            align: "end",
        },
        {
            text: "Total extra transport kilometres *",
            value: "road_delta_length_km",
            align: "end",
        },
    ];

    enterpriseTypeHeaders = [
        { text: "Type", value: "enterprise_type" },
        { text: "Category", value: "enterprise_category" },
        {
            text: "Baseline trips *",
            value: "baseline_exposed_trip_count",
            align: "end",
        },
        {
            text: "Baseline tonnes *",
            value: "baseline_exposed_tonnes",
            align: "end",
        },
        {
            text: "Impacted trips *",
            value: "impacted_exposed_trip_count",
            align: "end",
        },
        {
            text: "Impacted tonnes *",
            value: "impacted_exposed_tonnes",
            align: "end",
        },
        {
            text: "Rerouted trips *",
            value: "rerouted_exposed_trip_count",
            align: "end",
        },
        {
            text: "Rerouted tonnes *",
            value: "rerouted_exposed_tonnes",
            align: "end",
        },
        {
            text: "Blocked trips *",
            value: "blocked_exposed_trip_count",
            align: "end",
        },
        {
            text: "Blocked tonnes *",
            value: "blocked_exposed_tonnes",
            align: "end",
        },
    ];

    get commodityRows(): any[] {
        return Array.isArray(this.commodity.data) ? this.commodity.data : [];
    }
    get enterpriseTypeRows(): any[] {
        return Array.isArray(this.enterpriseTypes.data)
            ? this.enterpriseTypes.data
            : [];
    }
    get originEnterpriseTypes(): any[] {
        return this.enterpriseTypeRows.filter(
            (r) => r.enterprise_role === "origin",
        );
    }
    get destinationEnterpriseTypes(): any[] {
        return this.enterpriseTypeRows.filter(
            (r) => r.enterprise_role === "destination",
        );
    }
}
